import { Injectable } from '@angular/core';
import { DateRange } from 'app/trello/models/date-range';
import { BoardStatusFetcher } from 'app/trello/services/board-status-fetcher';
import { DateTime } from 'luxon';
import { map, Observable, shareReplay, take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ComparableDateRangeProvider {

  private readonly boardStatus = this.boardStatusFetcher.fetch()
    .pipe(
      take(1),
      shareReplay(),
    );

  constructor(
    private readonly boardStatusFetcher: BoardStatusFetcher
  ) {
  }

  public getComparableDateRange(
    dateRange: DateRange,
  ): Observable<DateRange | undefined> {

    return this.boardStatus
      .pipe(
        map((boardStatus) => {

          if (!boardStatus.firstSynchronisedEventAt) {
            console.warn('No comparable date range available: board not synchronised');
            return undefined;
          }

          const firstAvailableDate = DateTime.fromISO(boardStatus.firstSynchronisedEventAt, {setZone: true});

          const inputRangeFrom = dateRange.rangeFrom ? DateTime.fromISO(dateRange.rangeFrom, {setZone: true}) : undefined;
          const inputRangeTo = dateRange.rangeTo ? DateTime.fromISO(dateRange.rangeTo, {setZone: true}) : undefined;

          if (!inputRangeFrom || !inputRangeTo
            || inputRangeFrom > inputRangeTo) {
            console.warn('No comparable date range available: invalid input');
            return undefined;
          }

          if (!inputRangeFrom.equals(inputRangeFrom.startOf('day'))
            || !inputRangeTo.equals(inputRangeTo.endOf('day'))) {
            console.warn('No comparable date range available: input range not whole days');
            return undefined;
          }

          let comparableRangeFrom: DateTime;
          let comparableRangeTo: DateTime;

          switch (dateRange.rangeType) {

            case 'lastDays7':

              comparableRangeFrom = inputRangeFrom.minus({days: 7});
              comparableRangeTo = inputRangeTo.minus({days: 7});

              break;

            case 'lastDays14':

              comparableRangeFrom = inputRangeFrom.minus({days: 14});
              comparableRangeTo = inputRangeTo.minus({days: 14});

              break;

            case 'lastDays28':

              comparableRangeFrom = inputRangeFrom.minus({days: 28});
              comparableRangeTo = inputRangeTo.minus({days: 28});

              break;

            case 'lastYear':

              comparableRangeFrom = inputRangeFrom.minus({years: 1});
              comparableRangeTo = inputRangeTo.minus({years: 1});

              break;

            case 'weekSoFar':

              comparableRangeFrom = inputRangeFrom.minus({weeks: 1});
              comparableRangeTo = inputRangeTo.minus({weeks: 1});

              break;

            case 'monthSoFar':

              comparableRangeFrom = inputRangeFrom.minus({months: 1});
              comparableRangeTo = inputRangeTo.minus({months: 1});

              break;

            case 'yearSoFar':

              comparableRangeFrom = inputRangeFrom.minus({years: 1});
              comparableRangeTo = inputRangeTo.minus({years: 1});

              break;

            case 'previousWeek':

              comparableRangeFrom = inputRangeFrom.minus({weeks: 1}).startOf('week');
              comparableRangeTo = inputRangeTo.minus({weeks: 1}).endOf('week');

              break;

            case 'previousMonth':

              comparableRangeFrom = inputRangeFrom.minus({months: 1}).startOf('month');
              comparableRangeTo = inputRangeTo.minus({months: 1}).endOf('month');

              break;

            case 'previousYear':

              comparableRangeFrom = inputRangeFrom.minus({years: 1}).startOf('year');
              comparableRangeTo = inputRangeTo.minus({years: 1}).endOf('year');

              break;

            case 'custom':

              const duration = inputRangeTo.toMillis() - inputRangeFrom.toMillis() + 1;

              if (duration < 0) {
                console.warn('No comparable date range available: invalid input');
                return undefined;
              }

              comparableRangeFrom = inputRangeFrom.minus({milliseconds: duration});
              comparableRangeTo = inputRangeTo.minus({milliseconds: duration});

              break;

            default:
              console.warn('No comparable date range available: unsupported range type');
              return undefined;
          }

          if (comparableRangeFrom < firstAvailableDate) {
            console.warn('No comparable date range available: comparable data unavailable');
            return undefined;
          }

          const rangeFrom = comparableRangeFrom.toISO()!;
          const rangeTo = comparableRangeTo.toISO()!;

          return {
            rangeFrom: rangeFrom,
            rangeTo: rangeTo,
            rangeType: 'custom',
          };
        })
      );
  }

}
